import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle, ArrowClockwise, FileArrowDown, Receipt, CurrencyDollar,
} from "@phosphor-icons/react";
import { fmtDate, fmtDateTime } from "@/lib/dates";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function FinancePage() {
  const [tab, setTab] = useState("pending"); // 'pending' | 'invoiced'
  const [rows, setRows] = useState([]);
  const [remarks, setRemarks] = useState({}); // { "recordId-lineIdx": "remark text" }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(null); // row currently being invoiced
  const [showReport, setShowReport] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/chargeable", { params: { status: tab } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { setRemarks({}); }, [tab]);   // reset drafts on tab switch

  const rowKey = (r) => `${r.record_id}-${r.line_index}`;
  const setRowRemark = (r, val) => setRemarks((m) => ({ ...m, [rowKey(r)]: val }));
  const getRowRemark = (r) => remarks[rowKey(r)] ?? "";

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.receipt_no || "").toLowerCase().includes(q) ||
      (r.work_order_id || "").toLowerCase().includes(q) ||
      (r.site || "").toLowerCase().includes(q) ||
      (r.engineer_name || "").toLowerCase().includes(q) ||
      (r.part_name || "").toLowerCase().includes(q) ||
      (r.region || "").toLowerCase().includes(q) ||
      (r.invoice_no || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totals = useMemo(() => ({
    lines: rows.length,
    units: rows.reduce((s, r) => s + (r.quantity || 0), 0),
    ivs: new Set(rows.map((r) => r.receipt_no)).size,
  }), [rows]);

  const doInvoice = async (r) => {
    const remark = (getRowRemark(r) || "").trim();
    if (!remark) {
      toast.error("Enter a remark before invoicing");
      return;
    }
    try {
      await api.patch(`/records/${r.record_id}/invoice-line/${r.line_index}`, { remark });
      toast.success(`Marked ${r.part_name} on ${r.receipt_no} as invoiced`);
      setRemarks((m) => { const n = { ...m }; delete n[rowKey(r)]; return n; });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPending(null); }
  };

  const doUninvoice = async (r) => {
    if (!window.confirm(`Revert invoice ${r.invoice_no}? This should only be used for corrections.`)) return;
    try {
      await api.post(`/records/${r.record_id}/uninvoice-line/${r.line_index}`);
      toast.success(`Reverted invoice ${r.invoice_no}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-neutral-200 bg-white sticky top-0 z-10">
        <div className="px-10 pt-8 pb-4 flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow flex items-center gap-1.5">
              <CurrencyDollar size={12} weight="bold" /> Billing · Chargeable
            </div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Finance Dashboard</h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-xl">
              Every part flagged <span className="font-mono">Chargeable</span> at request-time lands here.
              Mark each line as <span className="font-mono">Invoiced</span> once you've raised the customer invoice — it moves to Billing&nbsp;History.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReport(true)}
              data-testid="monthly-report-btn"
              className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-neutral-50"
            >
              <FileArrowDown size={16} weight="bold" className="mr-2" /> Generate Monthly Report
            </Button>
            <Button
              variant="outline"
              onClick={refresh}
              disabled={refreshing}
              data-testid="finance-refresh"
              className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-neutral-50"
            >
              <ArrowClockwise size={16} weight="bold" className={`mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Tabs + summary */}
        <div className="px-10 pb-4 flex items-center gap-4 flex-wrap">
          <div className="flex border border-neutral-300" data-testid="finance-tabs">
            {[
              { k: "pending", l: "Pending Invoicing", icon: Receipt },
              { k: "invoiced", l: "Billing History", icon: CheckCircle },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setTab(t.k)}
                  data-testid={`finance-tab-${t.k}`}
                  className={`h-10 px-4 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-neutral-300 flex items-center gap-2 transition-colors ${
                    tab === t.k
                      ? "bg-neutral-900 text-white"
                      : "bg-white text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  <Icon size={12} weight="bold" /> {t.l}
                </button>
              );
            })}
          </div>
          <Input
            placeholder="Search IV, WO, site, technician, part, invoice…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="finance-search"
            className="h-10 rounded-none border-neutral-300 focus-visible:border-neutral-900 focus-visible:ring-0 flex-1 min-w-[240px] max-w-md"
          />
          <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-wider text-neutral-500 ml-auto">
            <div><span className="text-neutral-900 text-lg font-display">{totals.lines}</span> lines</div>
            <div><span className="text-neutral-900 text-lg font-display">{totals.units}</span> units</div>
            <div><span className="text-neutral-900 text-lg font-display">{totals.ivs}</span> IVs</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-10 py-6">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="finance-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">IV No</th>
                <th className="px-4 py-3 label-eyebrow">Date</th>
                <th className="px-4 py-3 label-eyebrow">Work Order</th>
                <th className="px-4 py-3 label-eyebrow">Site</th>
                <th className="px-4 py-3 label-eyebrow">Technician</th>
                <th className="px-4 py-3 label-eyebrow">Region</th>
                <th className="px-4 py-3 label-eyebrow">Part</th>
                <th className="px-4 py-3 label-eyebrow text-right">Qty</th>
                <th className="px-4 py-3 label-eyebrow">Remark</th>
                {tab === "invoiced" && <th className="px-4 py-3 label-eyebrow">Invoice</th>}
                {tab === "invoiced" && <th className="px-4 py-3 label-eyebrow">Invoiced</th>}
                <th className="px-4 py-3 label-eyebrow text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={tab === "invoiced" ? 12 : 10} className="px-4 py-10 text-center text-neutral-400">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={tab === "invoiced" ? 12 : 10} className="px-4 py-12 text-center text-neutral-400">
                  {tab === "pending" ? "No chargeable parts waiting to be invoiced." : "No billing history yet."}
                </td></tr>
              )}
              {filtered.map((r) => (
                <tr key={`${r.record_id}-${r.line_index}`} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`finance-row-${r.record_id}-${r.line_index}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.receipt_no}</td>
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{fmtDate(r.taken_at)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.work_order_id || <span className="text-neutral-300">—</span>}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`finance-site-${r.record_id}-${r.line_index}`}>
                    {r.site ? <span className="font-medium">{r.site}</span> : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-medium">{r.engineer_name}</div>
                    {r.engineer_employee_id && (
                      <div className="text-[10px] font-mono text-neutral-500">{r.engineer_employee_id} · {r.engineer_job_title}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">{r.region}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-medium">{r.part_name}</div>
                    <div className="flex gap-1 mt-0.5">
                      <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${
                        r.kind === "rb" ? "bg-amber-100 text-amber-800" : "bg-neutral-900 text-neutral-50"
                      }`}>{(r.kind || "new").toUpperCase()}</span>
                      {r.group && <span className="text-[9px] font-mono uppercase tracking-wider bg-neutral-100 px-1 py-0.5">{r.group}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-right">
                    {r.quantity}{r.uom ? <span className="text-neutral-400"> {r.uom}</span> : ""}
                  </td>
                  <td className="px-4 py-3">
                    {tab === "pending" ? (
                      <Input
                        value={getRowRemark(r)}
                        onChange={(e) => setRowRemark(r, e.target.value)}
                        placeholder="Required — e.g. PO123 / batch note"
                        data-testid={`remark-input-${r.record_id}-${r.line_index}`}
                        className="h-8 rounded-none border-neutral-400 text-xs min-w-[180px]"
                      />
                    ) : (
                      <div className="text-xs text-neutral-700 max-w-[220px] whitespace-normal">
                        {r.invoice_remark || <span className="text-neutral-300">—</span>}
                      </div>
                    )}
                  </td>
                  {tab === "invoiced" && (
                    <td className="px-4 py-3 font-mono text-xs">
                      <span className="bg-emerald-100 text-emerald-900 px-1.5 py-0.5">{r.invoice_no}</span>
                    </td>
                  )}
                  {tab === "invoiced" && (
                    <td className="px-4 py-3 font-mono text-[11px] text-neutral-600 whitespace-nowrap">
                      {fmtDateTime(r.invoiced_at)}
                      <div className="text-[9px] text-neutral-400">by {r.invoiced_by_name}</div>
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    {tab === "pending" ? (
                      <Button
                        size="sm"
                        onClick={() => setPending(r)}
                        disabled={!getRowRemark(r).trim()}
                        data-testid={`mark-invoiced-${r.record_id}-${r.line_index}`}
                        title={getRowRemark(r).trim() ? "Mark as invoiced" : "Enter a remark first"}
                        className="h-8 rounded-none bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-neutral-200 disabled:text-neutral-400 disabled:cursor-not-allowed text-xs"
                      >
                        <CheckCircle size={13} weight="bold" className="mr-1" /> Mark as Invoiced
                      </Button>
                    ) : (
                      <button
                        onClick={() => doUninvoice(r)}
                        data-testid={`uninvoice-${r.record_id}-${r.line_index}`}
                        className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 underline underline-offset-2 hover:text-red-700"
                      >
                        Revert
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation dialog */}
      {pending && (
        <ConfirmInvoiceDialog
          row={pending}
          remark={getRowRemark(pending)}
          onCancel={() => setPending(null)}
          onConfirm={() => doInvoice(pending)}
        />
      )}

      {/* Monthly report dialog */}
      {showReport && <MonthlyReportDialog onClose={() => setShowReport(false)} />}
    </div>
  );
}


function ConfirmInvoiceDialog({ row, remark, onCancel, onConfirm }) {
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="confirm-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Mark as Invoiced</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="border border-neutral-200 bg-neutral-50 p-3 space-y-1">
            <div className="text-xs"><span className="label-eyebrow inline-block w-24">IV</span> <span className="font-mono">{row.receipt_no}</span></div>
            <div className="text-xs"><span className="label-eyebrow inline-block w-24">Technician</span> {row.engineer_name}</div>
            <div className="text-xs"><span className="label-eyebrow inline-block w-24">Part</span> {row.part_name} × {row.quantity}</div>
            {row.work_order_id && (
              <div className="text-xs"><span className="label-eyebrow inline-block w-24">Work Order</span> <span className="font-mono">{row.work_order_id}</span></div>
            )}
            {row.site && (
              <div className="text-xs"><span className="label-eyebrow inline-block w-24">Site</span> {row.site}</div>
            )}
            <div className="text-xs"><span className="label-eyebrow inline-block w-24">Remark</span> <span className="italic">{remark || <span className="text-red-700">missing — cancel</span>}</span></div>
          </div>
          <p className="text-xs text-neutral-500">
            An invoice number (INV000001+) will be generated and this line will move to Billing&nbsp;History with your remark attached.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={onConfirm} disabled={!remark?.trim()} data-testid="confirm-invoice-submit" className="rounded-none bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-neutral-200 disabled:text-neutral-400">
            <CheckCircle size={14} weight="bold" className="mr-1.5" /> Confirm &amp; Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MonthlyReportDialog({ onClose }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(false);

  const years = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear(); y >= 2024; y--) arr.push(y);
    return arr;
  }, [now]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/finance/monthly-report.csv", {
        params: { year, month, status },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `chargeable-${year}-${String(month).padStart(2, "0")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded chargeable-${year}-${String(month).padStart(2, "0")}.csv`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none border-neutral-900" data-testid="monthly-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <FileArrowDown size={22} weight="duotone" /> Monthly Chargeable Report
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600">
            Exports every chargeable part-line issued in the selected calendar month, with WO&nbsp;ID, technician, region, part, qty, kind, invoice status &amp; notes.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Month</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger data-testid="report-month" className="rounded-none border-neutral-400"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger data-testid="report-year" className="rounded-none border-neutral-400"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Include</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="report-status" className="rounded-none border-neutral-400"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All chargeable lines</SelectItem>
                <SelectItem value="pending">Not yet invoiced</SelectItem>
                <SelectItem value="invoiced">Invoiced only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={download} disabled={busy} data-testid="report-download" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            <FileArrowDown size={14} weight="bold" className="mr-1.5" /> {busy ? "Preparing…" : "Download CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
